import Pusher from 'pusher-js';
import Api from './api';

const transformChannelName = (name, state) => {
  if (name === 'me') return `private-user-${state.loggedUser.user.id}`;
  return name;
};

function eventKey(channelName, eventName) {
  return `${channelName}::${eventName}`;
}

export default (() => {
  let _store;
  let _socket;
  let _events = {};

  const connectEvent = (event) => {
    if (!_socket || !event.channelName) return false;

    let channel = _socket.channels.channels[event.channelName];

    if (!channel) {
      channel = _socket.subscribe(event.channelName);

      if (process.env.NODE_ENV === 'development') {
        channel.bind('pusher:subscription_succeeded', () => console.log(`Pusher : Subscribed to ${event.channelName}`));
        channel.bind('pusher:subscription_error', (error) => console.log(`Pusher : failed ${event.channelName}`, error));
      }
    }

    const key = eventKey(event.channelName, event.eventName);

    if (_events[key]) {
      channel.unbind(event.eventName);
    }

    _events[key] = event;

    return channel.bind(event.eventName, (payload) => {
      Object.values(event.action).forEach((action) => action(payload));
    });
  };

  const getEvent = (channelName, eventName, action, scope) => {
    const key = eventKey(channelName, eventName);

    if (_events[key]) {
      return {
        eventName,
        channelName,
        action: {
          ..._events[key].action,
          [scope]: action,
        },
      };
    }

    return {
      eventName,
      channelName,
      action: { [scope]: action },
    };
  };

  const rebindEvent = (channelName, eventName) => {
    const key = eventKey(channelName, eventName);
    const event = _events[key];
    const channel = _socket?.channels?.channels?.[channelName];

    if (!event || !channel) return;

    channel.unbind(eventName);

    const actions = Object.values(event.action);
    if (!actions.length) {
      delete _events[key];
      return;
    }

    channel.bind(eventName, (payload) => {
      actions.forEach((action) => action(payload));
    });
  };

  return {
    getPusher: () => _socket,
    connect: (store) => (_store = store),
    initialise: async () => {
      if (_socket) return false;

      if (process.env.NODE_ENV === 'development') Pusher.logToConsole = true;

      _socket = new Pusher(process.env.PUSHER_KEY, {
        cluster: 'eu',
        encrypted: true,
        authorizer: (channel) => ({
          authorize: async (socketId, callback) => {
            try {
              const response = await Api.post('/v2/pusher/auth', {
                socket_id: socketId,
                channel_name: channel.name,
              });

              callback(null, {
                auth: response.auth,
                ...(response.channel_data ? { channel_data: response.channel_data } : {}),
              });
            } catch (err) {
              callback(true, err.status);
            }
          },
        }),
      });

      if (process.env.NODE_ENV === 'production' && window.Raven) {
        window.Raven.setExtraContext({ config: _socket.config });
        _socket.connection.bind('failed', (e) => window.Raven.captureException(new Error(e)));
      }

      return true;
    },
    registerEvent: (channel, eventName, action, scope = 'global') => {
      if (!_socket || !_store) return false;
      const channelName = transformChannelName(channel, _store.getState());
      const event = getEvent(channelName, eventName, action, scope);
      connectEvent(event);
      return action;
    },
    unregisterEvent: (channel, eventName, scope = 'global') => {
      if (!_socket || !_store) return false;

      const channelName = transformChannelName(channel, _store.getState());
      const key = eventKey(channelName, eventName);
      const event = _events[key];

      if (!event?.action?.[scope]) return false;

      delete event.action[scope];
      rebindEvent(channelName, eventName);

      return true;
    },
    disconnect: () => {
      if (_socket) _socket.disconnect();
      _socket = undefined;
      _events = {};
    },
    getChannels: () => _socket?.channels?.channels ?? {},
  };
})();
